import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { isMailProviderAuthError, handleMailProviderReconnect, getGooglePermissionError } from '../../utils/gmailOAuth';
import { getCid } from '../../utils/encryptedStorage';

/**
 * DraftMessageModal — Reusable modal for viewing, editing, and sending AI-generated email drafts.
 *
 * Used by:
 *  - Scene 2 (Reply to candidate question)
 *  - Scene 3 (Send follow-up message)
 *
 * Props:
 *  - isOpen        : boolean — controls visibility
 *  - onClose       : () => void
 *  - title         : string — modal heading ("Draft Reply" / "Follow-up Message")
 *  - subject       : string — email subject line
 *  - body          : string — AI-generated draft body
 *  - candidateEmail: string
 *  - candidateName : string
 *  - candidateQuestion : string | null — shown for reply scene
 *  - loading       : boolean — while AI is generating
 *  - sending       : boolean — while email is being sent
 *  - onSend        : (editedSubject, editedBody) => Promise<void>
 *  - onRegenerate  : (() => void) | null — optional regenerate callback
 *  - scene         : string — 'reply' | 'followup' (controls the "move to Interested" action)
 *  - onMoveToInterested : (() => Promise<void>) | null — optional; when provided and scene==='reply',
 *                          shows a "Not a question — move to Interested" button that reclassifies the
 *                          reply (no draft sent) and closes the modal.
 */
const DraftMessageModal = ({
  isOpen,
  onClose,
  title = 'Draft Message',
  subject: initialSubject = '',
  body: initialBody = '',
  candidateEmail = '',
  candidateName = '',
  candidateQuestion = '',
  loading = false,
  sending = false,
  onSend,
  onRegenerate = null,
  scene = '',
  onMoveToInterested = null,
}) => {
  const { settings } = useSettings();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  const [editedSubject, setEditedSubject] = useState(initialSubject);
  const [editedBody, setEditedBody] = useState(initialBody);
  const [originalBody, setOriginalBody] = useState(initialBody);
  const [isEdited, setIsEdited] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const [gmailConnecting, setGmailConnecting] = useState(false);
  const [movingToInterested, setMovingToInterested] = useState(false);
  const [movedToInterested, setMovedToInterested] = useState(false);
  const bodyRef = useRef(null);

  // Sync from props whenever new draft arrives
  useEffect(() => {
    setEditedSubject(initialSubject);
    setEditedBody(initialBody);
    setOriginalBody(initialBody);
    setIsEdited(false);
    setSent(false);
    setSendError('');
    setMovingToInterested(false);
    setMovedToInterested(false);
  }, [initialSubject, initialBody]);

  // Auto-resize textarea
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.style.height = 'auto';
      bodyRef.current.style.height = `${bodyRef.current.scrollHeight}px`;
    }
  }, [editedBody]);

  if (!isOpen) return null;

  const handleUndo = () => {
    setEditedBody(originalBody);
    setEditedSubject(initialSubject);
    setIsEdited(false);
  };

  const handleSend = async () => {
    if (!onSend) return;
    setSendError('');
    setGmailConnecting(false);
    try {
      await onSend(editedSubject, editedBody);
      setSent(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Send failed:', err);
      
      // Check if active mail provider is not connected — auto-trigger OAuth popup
      if (isMailProviderAuthError(err)) {
        setGmailConnecting(true);
        setSendError('Mail provider is not connected. Opening authorization...');
        const connected = await handleMailProviderReconnect({
          cid: getCid(),
          error: err,
          onConnecting: (providerLabel) => setSendError(`Connecting ${providerLabel}... Please complete authorization in the popup window.`),
          onConnected: (email, providerLabel) => {
            setSendError('');
            setGmailConnecting(false);
            // Auto-retry the send after successful connection
            setSendError(`${providerLabel} connected${email ? ` (${email})` : ''}! Click Send again to deliver the email.`);
          },
          onError: (msg) => {
            setGmailConnecting(false);
            setSendError(`Mail connection failed: ${msg}. Please go to Account → Credentials to connect Gmail or Outlook.`);
          },
        });
        if (!connected) {
          setGmailConnecting(false);
        }
        return;
      }
      
      // Check for Google permission errors
      const googlePermMsg = getGooglePermissionError(err);
      if (googlePermMsg) {
        setSendError(googlePermMsg);
        return;
      }

      // Extract user-readable message from API error
      const apiDetail = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.detail || '';
      if (apiDetail) {
        setSendError(apiDetail);
      } else if (err?.message) {
        setSendError(err.message);
      } else {
        setSendError('Failed to send email. Please try again.');
      }
    }
  };

  const handleMoveToInterested = async () => {
    if (!onMoveToInterested || movingToInterested) return;
    setSendError('');
    setMovingToInterested(true);
    try {
      await onMoveToInterested();
      setMovedToInterested(true);
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      console.error('Move to Interested failed:', err);
      const apiDetail = err?.response?.data?.detail || err?.detail || err?.message || '';
      setSendError(apiDetail || 'Could not move this candidate to Interested. Please try again.');
      setMovingToInterested(false);
    }
  };

  const showMoveToInterested = scene === 'reply' && typeof onMoveToInterested === 'function';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm dark:bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700"
          style={{ background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}08)` }}
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            {candidateName && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                To: {candidateName} {candidateEmail ? `(${candidateEmail})` : ''}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div
                className="w-10 h-10 rounded-full animate-spin"
                style={{ border: `3px solid ${primaryColor}30`, borderTopColor: primaryColor }}
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">Generating draft with AI…</p>
            </div>
          ) : (sent || movedToInterested) ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: `${secondaryColor}15` }}>
                <svg className="w-7 h-7" style={{ color: secondaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {movedToInterested ? 'Moved to Interested — ready to schedule.' : 'Email sent successfully!'}
              </p>
            </div>
          ) : (
            <>
              {/* Error banner */}
              {sendError && (
                <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">{sendError}</p>
                  </div>
                  <button
                    onClick={() => setSendError('')}
                    className="ml-auto flex-shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Candidate's question (Scene 2) */}
              {candidateQuestion && (
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Candidate's Question</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {candidateQuestion.length > 400 ? `${candidateQuestion.slice(0, 400)}…` : candidateQuestion}
                  </p>
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={editedSubject}
                  onChange={(e) => { setEditedSubject(e.target.value); setIsEdited(true); }}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-shadow"
                  style={{ focusRingColor: primaryColor }}
                  placeholder="Email subject"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Message</label>
                <textarea
                  ref={bodyRef}
                  value={editedBody}
                  onChange={(e) => { setEditedBody(e.target.value); setIsEdited(true); }}
                  rows={8}
                  className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-shadow resize-none leading-relaxed"
                  style={{ focusRingColor: primaryColor, minHeight: '200px' }}
                  placeholder="Email body"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        {!loading && !sent && !movedToInterested && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {/* Not a question — move to Interested (skip draft). Reclassifies the reply so the
                  candidate becomes schedulable; no email is sent. */}
              {showMoveToInterested && (
                <button
                  onClick={handleMoveToInterested}
                  disabled={movingToInterested}
                  className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: `${secondaryColor}55`, color: secondaryColor }}
                  title="Not actually a question — mark this candidate as Interested and skip the draft"
                >
                  {movingToInterested ? 'Moving…' : 'Not a question — move to Interested'}
                </button>
              )}
              {/* Undo */}
              {isEdited && (
                <button
                  onClick={handleUndo}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Undo Changes
                </button>
              )}
              {/* Regenerate */}
              {onRegenerate && (
                <button
                  onClick={onRegenerate}
                  className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
                  style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
                >
                  Regenerate
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !editedBody.trim()}
                className="px-6 py-2.5 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending…
                  </span>
                ) : (
                  'Send Email'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default DraftMessageModal;
